import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatTL, formatDateTR, todayISO } from "@/lib/format";
import { PageHeader, AddButton, Field, inputCls } from "@/components/shared";
import { usePermission } from "@/lib/permissions";
import FilterBar from "@/components/FilterBar";
import { downloadCSV, fmtDate, fmtNum } from "@/lib/csv";
import { Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Kira", "Personel", "Fatura", "Vergi", "Malzeme", "Pazarlama", "Ulaşım", "Diğer"];

const emptyForm = { title: "", amount: "", date: todayISO(), category: "Diğer", note: "" };

export default function Expenses() {
  const canEdit = usePermission("expenses", "edit");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/expenses");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((x) => {
      if (q && !`${x.title} ${x.note || ""}`.toLowerCase().includes(q)) return false;
      if (category && x.category !== category) return false;
      if (dateFrom && x.date < dateFrom) return false;
      if (dateTo && x.date > dateTo) return false;
      return true;
    });
  }, [items, search, category, dateFrom, dateTo]);

  const totalFiltered = useMemo(() => filtered.reduce((s, x) => s + (x.amount || 0), 0), [filtered]);

  const exportCSV = () => {
    downloadCSV(filtered, [
      { label: "Başlık", key: "title" },
      { label: "Kategori", key: "category" },
      { label: "Tarih", get: (r) => fmtDate(r.date) },
      { label: "Tutar (TL)", get: (r) => fmtNum(r.amount) },
      { label: "Not", key: "note" },
    ], `giderler_${todayISO()}.csv`);
  };

  const clearFilter = () => { setSearch(""); setCategory(""); setDateFrom(""); setDateTo(""); };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (x) => { setEditing(x); setForm({ ...x, amount: String(x.amount) }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editing) { await api.put(`/expenses/${editing.id}`, payload); toast.success("Gider güncellendi"); }
      else { await api.post("/expenses", payload); toast.success("Gider eklendi"); }
      setOpen(false); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu gideri silmek istediğinize emin misiniz?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader
        eyebrow="Nakit çıkışları"
        title="Giderler"
        description="Tüm giderlerinizi kategori bazında kaydedin ve takip edin."
        action={canEdit ? <AddButton onClick={openAdd} label="Yeni Gider" testid="add-expense-btn" /> : null}
      />

      <FilterBar
        search={search} onSearch={setSearch}
        status={category} onStatus={setCategory}
        statusOptions={CATEGORIES.map((c) => ({ value: c, label: c }))}
        dateFrom={dateFrom} onDateFrom={setDateFrom}
        dateTo={dateTo} onDateTo={setDateTo}
        onExport={exportCSV}
        onClear={clearFilter}
        searchPlaceholder="Başlık veya not…"
        testidPrefix="expenses-filter"
      />

      {(search || category || dateFrom || dateTo) ? (
        <div className="mb-6 bg-[#F0EFEA] border border-[#E6E4DD] rounded-xl px-5 py-3 flex items-center justify-between text-sm" data-testid="expenses-filter-total">
          <span className="text-[#5C5B57]">Filtrelenmiş kayıt: <strong className="text-[#2D2C2A]">{filtered.length}</strong></span>
          <span className="text-[#5C5B57]">Toplam: <strong className="text-[#B24C4C] font-mono">{formatTL(totalFiltered)}</strong></span>
        </div>
      ) : (
        <div data-testid="expenses-filter-total" style={{ display: "none" }} />
      )}

      <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="expenses-table">
            <thead>
              <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
                <th className="px-6 py-4">Başlık</th>
                <th className="px-6 py-4">Kategori</th>
                <th className="px-6 py-4">Tarih</th>
                <th className="px-6 py-4">Tutar</th>
                <th className="px-6 py-4">Not</th>
                <th className="px-6 py-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Henüz gider eklenmedi.</td></tr>}
              {items.length > 0 && filtered.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Filtreye uyan kayıt bulunamadı.</td></tr>}
              {filtered.map((x) => (
                <tr key={x.id} className="border-b border-[#E6E4DD]/60 hover:bg-[#F0EFEA]/40 transition-colors" data-testid={`expense-row-${x.id}`}>
                  <td className="px-6 py-4 text-[#2D2C2A] font-medium">{x.title}</td>
                  <td className="px-6 py-4">
                    <span className="inline-block text-xs px-2.5 py-1 rounded-full font-medium bg-[#F0EFEA] text-[#5C5B57]">{x.category}</span>
                  </td>
                  <td className="px-6 py-4 text-[#5C5B57]">{formatDateTR(x.date)}</td>
                  <td className="px-6 py-4 font-mono font-medium text-[#B24C4C]">-{formatTL(x.amount)}</td>
                  <td className="px-6 py-4 text-[#5C5B57] text-sm max-w-xs truncate">{x.note || "-"}</td>
                  <td className="px-6 py-4 text-right">
                    {canEdit && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => openEdit(x)} data-testid={`edit-expense-${x.id}`} className="p-2 rounded-lg hover:bg-[#F0EFEA] text-[#5C5B57]"><Pencil size={16} /></button>
                      <button onClick={() => remove(x.id)} data-testid={`delete-expense-${x.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]"><Trash2 size={16} /></button>
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="expense-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl text-[#2D2C2A]">{editing ? "Gider Düzenle" : "Yeni Gider"}</h3>
              <button onClick={() => setOpen(false)} className="text-[#5C5B57]"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Başlık"><input required className={inputCls} data-testid="expense-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tutar (₺)"><input required type="number" step="0.01" min="0" className={inputCls} data-testid="expense-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                <Field label="Tarih"><input required type="date" className={inputCls} data-testid="expense-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
              </div>
              <Field label="Kategori">
                <select className={inputCls} data-testid="expense-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Not"><textarea className={inputCls} rows={3} data-testid="expense-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 hover:bg-[#F0EFEA]">İptal</button>
                <button type="submit" data-testid="expense-submit" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">{editing ? "Güncelle" : "Ekle"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
